
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, Crown, Zap, Users, TrendingUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface FeaturingOption {
  id: string;
  name: string;
  description: string;
  price: string;
  duration: string;
  features: string[];
  icon: React.ReactNode;
  popular?: boolean;
}

const featuringOptions: FeaturingOption[] = [
  {
    id: 'basic',
    name: 'Basic Featuring',
    description: 'Get your startup noticed by teens',
    price: '$49',
    duration: '7 days',
    features: [
      'Featured in startup section',
      'Priority in search results',
      'Basic analytics',
      'Email support'
    ],
    icon: <Star className="h-6 w-6" />
  },
  {
    id: 'premium',
    name: 'Premium Featuring',
    description: 'Maximum visibility and engagement',
    price: '$149',
    duration: '30 days',
    features: [
      'Top featured placement',
      'Social media promotion',
      'Advanced analytics',
      'Community badge',
      'Priority support',
      'Newsletter inclusion'
    ],
    icon: <Crown className="h-6 w-6" />,
    popular: true
  },
  {
    id: 'boost',
    name: 'Community Boost',
    description: 'Promote your community to teens',
    price: '$99',
    duration: '14 days',
    features: [
      'Featured community',
      'Teen dashboard promotion',
      'Growth analytics',
      'Engagement tools'
    ],
    icon: <Users className="h-6 w-6" />
  }
];

const FeaturingCard: React.FC = () => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const { toast } = useToast();

  const handleContactSupport = () => {
    window.location.href = 'mailto:teenvisor@gmail.com?subject=Featuring Options Inquiry';
  };

  const handleSelectOption = (optionId: string) => {
    setSelectedOption(optionId);
    toast({
      title: "Featuring Option Selected",
      description: "Please contact our team to proceed with your featuring request.",
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-[#FF8C00] to-orange-500 bg-clip-text text-transparent">
          Get Featured on TeenVisor
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Boost your startup's visibility among teens with our featuring options. 
          Get discovered by the next generation of innovators!
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {featuringOptions.map((option) => (
          <Card 
            key={option.id}
            className={`relative transition-all duration-300 hover:shadow-lg cursor-pointer ${
              option.popular ? 'border-[#FF8C00] shadow-lg' : ''
            } ${selectedOption === option.id ? 'ring-2 ring-[#FF8C00]' : ''}`}
            onClick={() => handleSelectOption(option.id)}
          >
            {option.popular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-[#FF8C00] text-white px-3 py-1">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  Most Popular
                </Badge>
              </div>
            )}
            
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center mb-3">
                <div className={`p-3 rounded-full ${option.popular ? 'bg-[#FF8C00] text-white' : 'bg-gray-100 text-[#FF8C00]'}`}>
                  {option.icon}
                </div>
              </div>
              <CardTitle className="text-xl">{option.name}</CardTitle>
              <p className="text-muted-foreground text-sm">{option.description}</p>
              <div className="mt-4">
                <span className="text-3xl font-bold text-[#FF8C00]">{option.price}</span>
                <span className="text-muted-foreground">/{option.duration}</span>
              </div>
            </CardHeader>
            
            <CardContent>
              <ul className="space-y-2">
                {option.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-sm">
                    <Zap className="h-4 w-4 text-[#FF8C00] mr-2 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              
              <Button 
                className={`w-full mt-6 ${
                  option.popular 
                    ? 'bg-[#FF8C00] hover:bg-orange-600 text-white' 
                    : 'border-[#FF8C00] text-[#FF8C00] hover:bg-[#FF8C00] hover:text-white'
                }`}
                variant={option.popular ? 'default' : 'outline'}
              >
                Select Plan
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="text-center">
        <Card className="max-w-2xl mx-auto bg-gradient-to-r from-[#FF8C00]/10 to-orange-500/10 border-[#FF8C00]/20">
          <CardContent className="p-6">
            <h3 className="text-xl font-semibold mb-3">Need Custom Featuring?</h3>
            <p className="text-muted-foreground mb-4">
              Looking for a custom featuring package or have questions about our options? 
              Our team is here to help you maximize your startup's impact.
            </p>
            <Button 
              onClick={handleContactSupport}
              className="bg-[#FF8C00] hover:bg-orange-600 text-white"
            >
              Contact TeenVisor Team
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FeaturingCard;
